/**
 * The workspace roles allowed to perform writes guarded by `withWriter`
 * (superadmin | admin | editor | accountant).
 *
 * One list for every surface that performs the same write - the HTTP guard,
 * an AI chat tool, and the client deciding which controls to offer - so they
 * can never disagree (OCT-15906).
 */
export const WRITER_ROLES = ["superadmin", "admin", "editor", "accountant"] as const;

/** True when `role` is one `withWriter` admits. */
export const isWriterRole = (role: string | null | undefined): boolean =>
  !!role && (WRITER_ROLES as readonly string[]).includes(role);

/** The read-only workspace role. */
export const VIEWER_ROLE = "viewer" as const;

/**
 * True only for the Viewer role (OCT-17302).
 *
 * The product-wide "may not change workspace data" rule targets the Viewer
 * role explicitly. It is never "not in the writer list": the legacy `member`
 * role, an unknown role and a missing role are NOT viewers, so they keep
 * exactly the access they had before OCT-16925. The central HTTP check, the
 * AI chat tool runner and the client's `useCanWrite()` all read this one
 * predicate so they can never disagree.
 *
 * @spec ILLEGAL E157
 */
export const isViewerRole = (role: string | null | undefined): boolean => role === VIEWER_ROLE;

/**
 * The workspace roles `withAdmin` admits (OCT-21990). One list for the HTTP
 * guard and the UI deciding which admin-only controls to offer (the
 * API & Webhooks tab), so the two can never drift.
 */
export const ADMIN_ROLES = ["superadmin", "admin"] as const;

/** True when `role` is one `withAdmin` admits. */
export const isAdminRole = (role: string | null | undefined): boolean =>
  !!role && (ADMIN_ROLES as readonly string[]).includes(role);

/**
 * The workspace roles allowed to redeem (and list) credit coupons - the
 * `withRole(...)` guard on `POST /api/billing/credit-coupons/redeem` and
 * `GET /api/billing/credit-coupons` (OCT-22452).
 *
 * Why it differs from `ADMIN_ROLES`: the `owner` and `billing_contact` roles
 * may redeem credit coupons, but they may not buy plans or manage billing
 * (those stay `withAdmin`). The list predates OCT-22452; it was moved to
 * `shared/` so the coupon route and the UI deciding whether to offer the
 * Redeem control read one list and can never drift. Named for what it grants
 * (coupon redeem), not "billing admin", so it is not mistaken for the billing
 * admin guard.
 *
 * @spec ILLEGAL E165
 */
export const COUPON_REDEEM_ROLES = ["admin", "owner", "billing_contact", "superadmin"] as const;

/** True when `role` is one the credit-coupon guard admits. */
export const isCouponRedeemRole = (role: string | null | undefined): boolean =>
  !!role && (COUPON_REDEEM_ROLES as readonly string[]).includes(role);

/**
 * `details.reason` on the `403 FORBIDDEN` a role guard (`withRole` /
 * `withWriter`) returns (OCT-21990). Other 403s share the `FORBIDDEN` code -
 * a feature gate (`details.upgrade`), a CSRF mismatch, a foreign entity - so
 * the UI keys "your role can't do this" off this marker, never the status
 * or the message.
 */
export const INSUFFICIENT_ROLE_REASON = "insufficient_role" as const;

/**
 * True when an error envelope (`code` + `details`) is a role guard refusing
 * the caller (OCT-22449): the central Viewer check (`VIEWER_READ_ONLY`), or
 * `FORBIDDEN` with `details.reason: "insufficient_role"` (`withAdmin`,
 * `withRole`, `withWriter`). Every other 403 shares the `FORBIDDEN` code - a
 * feature gate (`details.upgrade`), a CSRF mismatch, a foreign entity - and is
 * not a role refusal.
 *
 * @spec ILLEGAL E165
 */
export const isRoleRefusalEnvelope = (code: unknown, details: unknown): boolean => {
  if (code === "VIEWER_READ_ONLY") return true;
  if (code !== "FORBIDDEN") return false;
  if (!details || typeof details !== "object") return false;
  return (details as { reason?: unknown; }).reason === INSUFFICIENT_ROLE_REASON;
};
